/**
 * /ai-connect — the browser half of connecting a local AI client, like
 * `gh auth login`. The client opens this page with its loopback port, a state
 * nonce and a PKCE challenge. Connect asks the server for a one-time code and
 * sends the browser back to the client on 127.0.0.1. Only that code travels
 * in the URL, and it is worthless without the verifier the client kept.
 *
 * The address is always 127.0.0.1 plus a validated port, never a URL taken
 * from the query string, so the page cannot be turned into an open redirect.
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Button, MessageBar, MessageBarBody, Subtitle1, Text, makeStyles, tokens,
} from '@fluentui/react-components'
import {
  BoardRegular, DocumentSearchRegular, PersonRegular, PlugConnectedRegular,
} from '@fluentui/react-icons'
import api from '../lib/api'
import { apiErrorMessage } from '../lib/apiError'
import { useAuth } from '../context/AuthContext'
import { InfoHint } from '../components/planning/PlanParts'

const STATE = /^[A-Za-z0-9_-]{16,128}$/
const CHALLENGE = /^[A-Za-z0-9_-]{43}$/

const HINT =
  'Lasts 24 hours; the AI tool opens this page again when it runs out. ' +
  'Connecting replaces any other AI connection on your account. ' +
  'Revoke it in My profile → AI access. Use only an approved AI client.'

const useStyles = makeStyles({
  page: {
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    width: '100%', maxWidth: '440px', display: 'flex', flexDirection: 'column',
    gap: tokens.spacingVerticalL, padding: tokens.spacingHorizontalXXL,
    borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
  },
  head: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  mark: { fontSize: '28px', color: tokens.colorBrandForeground1 },
  who: { color: tokens.colorNeutralForeground3 },
  list: {
    listStyle: 'none', margin: 0, padding: 0,
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS,
  },
  item: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  icon: { fontSize: '20px', flexShrink: 0, color: tokens.colorNeutralForeground2 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS },
})

/** The AI client's loopback address. Never built from a URL in the query string. */
export function loopbackUrl(port: number, params: Record<string, string>): string {
  return `http://127.0.0.1:${port}/callback?${new URLSearchParams(params).toString()}`
}

export default function AiConnect() {
  const styles = useStyles()
  const [params] = useSearchParams()
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const port = Number(params.get('port'))
  const state = params.get('state') ?? ''
  const challenge = params.get('challenge') ?? ''
  const valid =
    Number.isInteger(port) && port >= 1024 && port <= 65535 &&
    STATE.test(state) && CHALLENGE.test(challenge)
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  async function connect() {
    setBusy(true)
    setError('')
    try {
      const { data } = await api.post<{ code: string }>('/auth/ai-connect/code', {
        code_challenge: challenge,
      })
      window.location.assign(loopbackUrl(port, { code: data.code, state }))
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not connect. Try again.'))
      setBusy(false)
    }
  }

  function cancel() {
    window.location.assign(loopbackUrl(port, { error: 'access_denied', state }))
  }

  return (
    <main className={styles.page}>
      <section className={styles.card} aria-labelledby="ai-connect-title">
        <div className={styles.head}>
          <PlugConnectedRegular className={styles.mark} aria-hidden />
          <Subtitle1 as="h1" id="ai-connect-title">Connect an AI assistant</Subtitle1>
          <InfoHint text={HINT} />
        </div>
        {!valid ? (
          <MessageBar intent="warning">
            <MessageBarBody>
              This link did not come from the DACcelerate AI tool. Run login there again.
            </MessageBarBody>
          </MessageBar>
        ) : (
          <>
            <Text className={styles.who}>As {user?.name} · {user?.email}</Text>
            <ul className={styles.list} aria-label="It can">
              {isAdmin && (
                <li className={styles.item}>
                  <BoardRegular className={styles.icon} aria-hidden />
                  <Text>Read and change the planning board, as the page does</Text>
                </li>
              )}
              <li className={styles.item}>
                <DocumentSearchRegular className={styles.icon} aria-hidden />
                <Text>Read the demands you can see</Text>
              </li>
              <li className={styles.item}>
                <PersonRegular className={styles.icon} aria-hidden />
                <Text>Read your profile, skills and applications</Text>
              </li>
            </ul>
            {error && (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            )}
            <div className={styles.actions}>
              <Button appearance="primary" disabled={busy} onClick={() => void connect()}>
                Connect
              </Button>
              <Button disabled={busy} onClick={cancel}>Cancel</Button>
            </div>
          </>
        )}
      </section>
    </main>
  )
}
